import { useMemo, useState } from 'react';
import { MfaSettings } from '@kawai/auth-bootstrap';
import { Button, Chip, Select, ZebraTable, type ChipTone } from '@kawai/ui';
import {
  canPurgeTenant,
  nowIsoJst,
  type Tenant,
  type TenantFeature,
  type TenantStatus,
} from '@kawai/shared';
import { useTenants } from '../lib/hooks/useTenants.js';
import { useImpersonate } from '../lib/hooks/useImpersonate.js';
import { useTenantSetStatus, useTenantPurge } from '../lib/hooks/useTenantLifecycle.js';
import { useAuth } from '../lib/auth.js';
import { impersonationErrorMessage } from '../lib/api/issueImpersonationToken.js';
import { tenantLifecycleErrorMessage } from '../lib/api/tenantLifecycle.js';
import { buildImpersonationUrl } from '../lib/impersonationUrl.js';
import {
  CARD,
  MONO,
  ROW_BTN_BRAND,
  ROW_BTN_DANGER,
  ROW_BTN_GHOST,
  ROW_BTN_WARNING,
} from '../lib/uiClasses.js';
import { PageHeader } from '../components/PageHeader.js';
import { Notice } from '../components/Notice.js';

type StatusFilter = 'all' | TenantStatus;
type FeatureFilter = 'all' | TenantFeature;

const ALL_STATUSES: StatusFilter[] = ['all', 'active', 'suspended', 'archived'];
const ALL_FEATURES: FeatureFilter[] = ['all', 'reservation', 'fuel', 'richmenu', 'promo'];

/** S-137: テナント状態の Chip 色（利用中 = 良い / 停止 = 注意 / 契約終了 = 中立）。 */
function tenantStatusTone(status: TenantStatus): ChipTone {
  switch (status) {
    case 'active':
      return 'success';
    case 'suspended':
      return 'warning';
    case 'archived':
      return 'neutral';
  }
}

/**
 * S-07: ブラウザ遷移操作を関数で隔離。テストでは props 経由で差し替える。
 *
 * 本番は **別タブ** (`window.open` w/ noopener) で開く:
 *   - saas-admin タブの Firebase Auth セッションを維持したまま、tenant-portal で
 *     impersonation セッションを並走させる
 *   - 'noopener' で opener 参照を切り、tampering / reverse tabnabbing 防止
 *   - popup blocker 対策のため、必ずユーザー操作 (button click) 起点で呼ぶこと
 */
type Navigator = (url: string) => void;
const defaultNavigator: Navigator = (url) => {
  window.open(url, '_blank', 'noopener');
};

export interface TenantsListPageProps {
  /**
   * S-07 テスト用: 「なりすましログイン」成功後に飛ばす navigator を差し替える。
   * 未指定時は `window.location.href = url` を使う。
   */
  navigator?: Navigator;
  /**
   * S-07 テスト用: 環境判定 (`import.meta.env.MODE` の上書き)。
   * 未指定時は `import.meta.env.MODE` を読む。
   */
  envMode?: string;
}

/**
 * S-05: テナント一覧（read 専用）。
 * S-07: 各 row に「なりすましログイン」ボタンを追加（active テナントのみ）。
 * S-137: 見た目は docs/UI-GUIDE.md の v2（白カード 30/26・ZebraTable・Chip・行内ボタンは面の色差）。
 *
 * - tenants collection 全件 fetch（saas-admin Rules で許可）
 * - status / feature でクライアント側フィルタ
 * - impersonation は active 状態の tenant のみ可能（suspended / archived は disabled）
 */
export function TenantsListPage({ navigator, envMode }: TenantsListPageProps = {}) {
  const { email, signOut, user, mfaEnrolled, refreshClaims } = useAuth();
  const { data, isLoading, error, refetch, isFetching } = useTenants();
  const impersonate = useImpersonate();
  const setStatus = useTenantSetStatus();
  const purge = useTenantPurge();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [featureFilter, setFeatureFilter] = useState<FeatureFilter>('all');
  const [impersonatingTenantId, setImpersonatingTenantId] = useState<string | null>(null);
  const [impersonationError, setImpersonationError] = useState<string | null>(null);
  const [lifecycleError, setLifecycleError] = useState<string | null>(null);
  const [lifecycleBusyTenantId, setLifecycleBusyTenantId] = useState<string | null>(null);

  const navigate = navigator ?? defaultNavigator;
  const mode = envMode ?? import.meta.env.MODE;

  // S-71: purge 可否（救済期限経過判定）のための現在時刻。JST 基準 Iso8601（プロジェクト規約）。
  const nowIso = nowIsoJst();

  const lifecyclePending = setStatus.isPending || purge.isPending;

  // S-71: status 遷移（停止 / 契約終了 / 再開 / 復活）。
  async function handleSetStatus(tenant: Tenant, next: TenantStatus, verb: string) {
    if (lifecyclePending) return;
    let message = `テナント "${tenant.id}" を「${verb}」します。続行しますか？`;
    if (next === 'archived') {
      message =
        `テナント "${tenant.id}" を契約終了（archived）にします。\n\n` +
        '・終了日から30日間はデータ救済（エクスポート）が可能です\n' +
        '・救済期間の経過後に「削除」が可能になります\n\n' +
        '続行しますか？';
    }
    if (!window.confirm(message)) return;
    setLifecycleError(null);
    setLifecycleBusyTenantId(tenant.id);
    try {
      await setStatus.mutateAsync({ tenantId: tenant.id, status: next });
    } catch (err) {
      setLifecycleError(tenantLifecycleErrorMessage(err));
    } finally {
      setLifecycleBusyTenantId(null);
    }
  }

  // S-71: テナント単位の利用者データ削除（救済期間経過後のみ）。confirmTenantId 再入力を要求。
  async function handlePurge(tenant: Tenant) {
    if (lifecyclePending) return;
    const typed = window.prompt(
      `テナント "${tenant.id}" の利用者データを完全削除します。\n\n` +
        '・予約 / 顧客 / 行動ログ等の個人データを物理削除します（取り消し不可）\n' +
        '・マスタ（店舗 / スタッフ / メニュー）と監査ログは保持されます\n\n' +
        '確認のため tenantId を入力してください:',
    );
    if (typed === null) return;
    if (typed !== tenant.id) {
      setLifecycleError('入力した tenantId が一致しません。削除を中止しました。');
      return;
    }
    setLifecycleError(null);
    setLifecycleBusyTenantId(tenant.id);
    try {
      await purge.mutateAsync({ tenantId: tenant.id, confirmTenantId: typed });
    } catch (err) {
      setLifecycleError(tenantLifecycleErrorMessage(err));
    } finally {
      setLifecycleBusyTenantId(null);
    }
  }

  async function handleImpersonate(tenant: Tenant) {
    if (tenant.status !== 'active') return;
    if (impersonate.isPending) return;
    const confirmed = window.confirm(
      `テナント "${tenant.id}" になりすましログインします。\n\n` +
        '・閲覧専用 (write 全拒否)\n' +
        '・全操作は audit_logs に記録\n' +
        '・別タブで tenant-portal が開きます\n\n' +
        '続行しますか？',
    );
    if (!confirmed) return;
    setImpersonationError(null);
    setImpersonatingTenantId(tenant.id);
    try {
      const res = await impersonate.mutateAsync({ targetTenantId: tenant.id });
      const url = buildImpersonationUrl({
        customToken: res.customToken,
        mode,
      });
      navigate(url);
    } catch (err) {
      setImpersonationError(impersonationErrorMessage(err));
    } finally {
      setImpersonatingTenantId(null);
    }
  }

  const filtered = useMemo<Tenant[]>(() => {
    if (!data) return [];
    return data.filter((t) => {
      if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      if (featureFilter !== 'all' && !t.features.includes(featureFilter)) return false;
      return true;
    });
  }, [data, statusFilter, featureFilter]);

  return (
    <main className="mx-auto max-w-[1100px] p-6">
      <PageHeader
        title="テナント一覧"
        description="SaaS 管理者ビュー（read 専用 / S-05）"
        email={email}
        links={[
          { to: '/billing', label: '課金' },
          { to: '/audit-logs', label: '監査ログ' },
          { to: '/export', label: 'エクスポート' },
        ]}
        primary={{ to: '/tenants/new', label: '＋ テナント追加' }}
        onSignOut={() => {
          void signOut();
        }}
      />

      <section className={`mt-6 ${CARD}`} aria-label="テナント">
        <div
          className="flex flex-wrap items-center gap-4"
          role="group"
          aria-label="フィルタ"
        >
          <label className="flex items-center gap-2 text-label text-ink-2">
            status:
            <Select
              size="sm"
              fullWidth={false}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            >
              {ALL_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </label>

          <label className="flex items-center gap-2 text-label text-ink-2">
            feature:
            <Select
              size="sm"
              fullWidth={false}
              value={featureFilter}
              onChange={(e) => setFeatureFilter(e.target.value as FeatureFilter)}
            >
              {ALL_FEATURES.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </Select>
          </label>

          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="ml-auto"
            onClick={() => {
              void refetch();
            }}
            disabled={isFetching}
          >
            {isFetching ? '取得中…' : '再取得'}
          </Button>
        </div>

        {isLoading && (
          <p role="status" aria-live="polite" className="mt-4 text-body text-ink-3">
            読み込み中…
          </p>
        )}

        {error && (
          <Notice role="alert" className="mt-4">
            テナント一覧の取得に失敗しました: {error.message}
          </Notice>
        )}

        {impersonationError && (
          <Notice role="alert" className="mt-4">
            {impersonationError}
          </Notice>
        )}

        {lifecycleError && (
          <Notice role="alert" className="mt-4">
            {lifecycleError}
          </Notice>
        )}

        {!isLoading && !error && (
          <div className="mt-4 overflow-x-auto">
            <ZebraTable aria-label="テナント一覧">
              <thead>
                <ZebraTable.HeadRow>
                  <ZebraTable.Th edge="first">tenantId</ZebraTable.Th>
                  <ZebraTable.Th>name</ZebraTable.Th>
                  <ZebraTable.Th>shopName</ZebraTable.Th>
                  <ZebraTable.Th>status</ZebraTable.Th>
                  <ZebraTable.Th>features</ZebraTable.Th>
                  <ZebraTable.Th>contractStartedAt</ZebraTable.Th>
                  <ZebraTable.Th edge="last">操作</ZebraTable.Th>
                </ZebraTable.HeadRow>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <ZebraTable.Row>
                    <ZebraTable.Td colSpan={7} align="center" tone="muted" padding="lg">
                      該当するテナントがありません
                    </ZebraTable.Td>
                  </ZebraTable.Row>
                ) : (
                  filtered.map((t) => {
                    const busy = impersonatingTenantId === t.id && impersonate.isPending;
                    const disabled = t.status !== 'active' || impersonate.isPending;
                    const purgeBlocked = lifecyclePending || !canPurgeTenant(t, nowIso);
                    return (
                      <ZebraTable.Row key={t.id} className="align-top">
                        <ZebraTable.Td className={MONO}>{t.id}</ZebraTable.Td>
                        <ZebraTable.Td>{t.name}</ZebraTable.Td>
                        <ZebraTable.Td>{t.shopName}</ZebraTable.Td>
                        <ZebraTable.Td>
                          <Chip tone={tenantStatusTone(t.status)}>{t.status}</Chip>
                        </ZebraTable.Td>
                        <ZebraTable.Td>
                          <div className="flex flex-wrap gap-1">
                            {t.features.map((f) => (
                              <Chip key={f} tone="brand">
                                {f}
                              </Chip>
                            ))}
                          </div>
                        </ZebraTable.Td>
                        <ZebraTable.Td className={`${MONO} whitespace-nowrap`}>
                          {t.contractStartedAt}
                        </ZebraTable.Td>
                        <ZebraTable.Td>
                          <div className="flex flex-col items-start gap-1.5">
                            <button
                              type="button"
                              onClick={() => {
                                void handleImpersonate(t);
                              }}
                              disabled={disabled}
                              aria-label={`${t.id} になりすましログイン`}
                              title={
                                t.status === 'active'
                                  ? 'tenant-portal を別タブで開いて閲覧モードでログインします'
                                  : 'active テナントのみ impersonation 可能です'
                              }
                              className={ROW_BTN_BRAND}
                            >
                              {busy ? '発行中…' : 'なりすましログイン'}
                            </button>
                            <div className="flex flex-wrap gap-1">
                              {t.status === 'active' && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    void handleSetStatus(t, 'suspended', '一時停止')
                                  }
                                  disabled={lifecyclePending}
                                  aria-label={`${t.id} を一時停止`}
                                  className={ROW_BTN_GHOST}
                                >
                                  停止
                                </button>
                              )}
                              {t.status === 'suspended' && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    void handleSetStatus(t, 'active', '再開')
                                  }
                                  disabled={lifecyclePending}
                                  aria-label={`${t.id} を再開`}
                                  className={ROW_BTN_GHOST}
                                >
                                  再開
                                </button>
                              )}
                              {(t.status === 'active' || t.status === 'suspended') && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    void handleSetStatus(t, 'archived', '契約終了')
                                  }
                                  disabled={lifecyclePending}
                                  aria-label={`${t.id} を契約終了`}
                                  title="契約終了（archived）。終了日から30日の救済期間を起算します"
                                  className={ROW_BTN_WARNING}
                                >
                                  契約終了
                                </button>
                              )}
                              {t.status === 'archived' && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      void handleSetStatus(t, 'active', '復活')
                                    }
                                    disabled={lifecyclePending}
                                    aria-label={`${t.id} を復活`}
                                    className={ROW_BTN_GHOST}
                                  >
                                    復活
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => void handlePurge(t)}
                                    disabled={purgeBlocked}
                                    aria-label={`${t.id} の利用者データを削除`}
                                    title={
                                      canPurgeTenant(t, nowIso)
                                        ? '利用者データを物理削除します（取り消し不可）'
                                        : '救済期間（30日）の経過後に削除できます'
                                    }
                                    className={ROW_BTN_DANGER}
                                  >
                                    {lifecycleBusyTenantId === t.id && purge.isPending
                                      ? '削除中…'
                                      : '削除'}
                                  </button>
                                </>
                              )}
                            </div>
                            {t.status === 'archived' && t.graceUntil && (
                              <span className={`${MONO} text-ink-3`}>
                                救済期限: {t.graceUntil.slice(0, 10)}
                              </span>
                            )}
                          </div>
                        </ZebraTable.Td>
                      </ZebraTable.Row>
                    );
                  })
                )}
              </tbody>
            </ZebraTable>
          </div>
        )}
      </section>

      {/* S-75: 2 段階認証（任意 / opt-in）。MFA はアカウント単位のため、ここで登録すれば
          全管理 app のサインインで challenge が有効になる。v2 の白カードは MfaSettings 自身が描く。 */}
      <section aria-label="セキュリティ" className="mt-6 max-w-[400px]">
        <MfaSettings
          user={user}
          enrolled={mfaEnrolled}
          issuer="TracePit SaaS 管理"
          accountName={email ?? 'admin'}
          onChange={refreshClaims}
          variant="v2"
        />
      </section>
    </main>
  );
}
